import { useEffect, useState } from "react";

import { EditorState } from "prosemirror-state";
import { Node } from "prosemirror-model";
import {
  ProseMirror,
  ProseMirrorDoc,
  reactKeys,
} from "@handlewithcare/react-prosemirror";

import { LoadingSpinner } from "@newsela/angelou";

import { schema } from "@/ai/components/Editor/schemas";

import { useBundleDataContext } from "@/bundles/providers/useBundleDataContext";
import { BookmarkContextProvider } from "@/users/useBookmarkContent/useBookmarkContent";

import { FLAVORS } from "@/components/ContentRow/ContentRow";
import wrapProsemirrorCardContainer from "@/bundles/utils/wrapProsemirrorCardContainer";
import HeaderActions from "../HeaderActions/HeaderActions";
import Viewmodel from "./components/Viewmodel/Viewmodel";
import { titleCss, prosemirrorContainerCss } from "./MainContent.styles";

type MainContentProps = {
  currentContentId: string | undefined;
};

const MainContent = ({ currentContentId }: MainContentProps) => {
  const { content, contentLoading, contentRequestError } =
    useBundleDataContext();

  const [currentDocument, setCurrentDocument] = useState<
    Record<string, any> | undefined
  >(undefined);
  const [currentEditorState, setCurrentEditorState] = useState<
    EditorState | undefined
  >(undefined);

  useEffect(() => {
    if (content && !contentLoading) {
      setCurrentDocument(content.page_viewmodel?.["documents"][0]);
    }
  }, [content, contentLoading]);

  useEffect(() => {
    if (currentDocument) {
      const currentDoc = currentDocument["body"];
      const docNode = Node.fromJSON(schema, currentDoc);
      const normalizedDoc = wrapProsemirrorCardContainer(docNode, schema);

      const editorState = EditorState.create({
        schema,
        doc: normalizedDoc,
        plugins: [reactKeys()],
      });
      setCurrentEditorState(editorState);
    }
  }, [currentDocument]);

  if (!currentEditorState || !currentDocument || contentLoading) {
    return <LoadingSpinner />;
  }

  if (contentRequestError) {
    return (
      <>
        <h1>Error when loading content.</h1>
      </>
    );
  }

  return (
    <BookmarkContextProvider>
      <h1 css={titleCss}>{currentDocument["title"]}</h1>
      <div css={prosemirrorContainerCss}>
        <HeaderActions content={content} currentContentId={currentContentId} />
        <ProseMirror
          editable={() => false}
          state={currentEditorState}
          dispatchTransaction={(tr) => {
            // Keep selection state in sync so selectionToDOM() renders the
            // user's selection correctly. Block document-mutating transactions
            // to preserve read-only behavior.
            if (!tr.docChanged) {
              setCurrentEditorState((prev) => prev?.apply(tr));
            }
          }}
          nodeViews={{
            viewmodel: (props) => (
              // @ts-ignore
              <Viewmodel {...props} flavor={FLAVORS.BUNDLE_ROW} />
            ),
          }}
        >
          <ProseMirrorDoc />
        </ProseMirror>
      </div>
    </BookmarkContextProvider>
  );
};

export default MainContent;
